import React from 'react'
import { SelectPopover } from '../common/select-popover';
import { useTranslation } from 'react-i18next';
import { GlobeIcon } from './assets/globe-icon';
import { cn } from '@/lib/utils';
import { Tooltip } from '../ui/modal';

export const OUT_LANG_OPTIONS = [
  { label: '香港繁体', value: 'zh-HK' },
  { label: '简体中文', value: 'zh-CN' },
  { label: 'English', value: 'en-US' },
  { label: '泰语', value: 'th-TH' },
] as const

/** 输出语言选择 */
export const OutputLangSelect: React.FC<{
  outLang: string;
  disabled?: boolean;
  setOutLang: (val: string) => void;
  /** 自定义触发器；不传则使用默认地球图标 + 语言文案 */
  trigger?: React.ReactNode;
  popoverSide?: 'top' | 'bottom' | 'left' | 'right';
  popoverAlign?: 'start' | 'end' | 'center';
  triggerHidden?: boolean;
  selectClassName?: string;
  sideOffset?: number;
  /** 侧向浮层时沿对齐轴微调（如 side=right 时常用负值略上移） */
  popoverAlignOffset?: number;
  triggerClassName?: string;
  optionsContainerClassName?: string;
  itemClassName?: string;
  anchorToTriggerEnd?: boolean;
  avoidCollisions?: boolean;
}> = ({
  outLang,
  disabled,
  setOutLang,
  trigger,
  popoverSide = 'top',
  popoverAlign = 'center',
  triggerHidden = true,
  selectClassName,
  sideOffset = 8,
  popoverAlignOffset = 0,
  triggerClassName,
  optionsContainerClassName,
  itemClassName,
  anchorToTriggerEnd,
  avoidCollisions,
}) => {
  const { t } = useTranslation();

  const defaultTrigger = (
    <div
      className={cn(
        'p-2 flex items-center justify-center rounded-[8px] hover:bg-[rgba(242,244,247,1)] transition-colors gap-1.5 cursor-pointer select-none',
        disabled && 'cursor-not-allowed opacity-50',
      )}
    >
      <GlobeIcon className="flex-shrink-0" />
      <span className="text-sm text-[rgba(16,24,40,1)] font-medium">
        {OUT_LANG_OPTIONS.find((o) => o.value === outLang)?.label}
      </span>
    </div>
  );

  return (
    <SelectPopover
      title={t('ai_creation.select_output_language')}
      trigger={
        <div className="w-full min-w-0">
          <Tooltip
            side="top"
            align="center"
            trigger={trigger ?? defaultTrigger}
            content={
              disabled ? (
                <div className="relative bg-[rgba(16,24,40,1)] px-3 py-2 rounded-[8px]">
                  <span className="text-[rgba(255,255,255,1)] text-sm font-medium">
                    使用前请关闭口播模式
                  </span>
                  <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent border-t-black" />
                </div>
              ) : null
            }
          />
        </div>
      }
      className={
        selectClassName ??
        'min-w-[256px] min-h-[171px] rounded-[16px] border border-[rgba(234,236,240,1)] bg-white [box-shadow:0px_4px_6px_-2px_rgba(16,_24,_40,_0.03),_0px_12px_16px_-4px_rgba(16,_24,_40,_0.08)]'
      }
      itemClassName={itemClassName}
      options={[...OUT_LANG_OPTIONS]}
      value={outLang}
      disabled={disabled}
      side={popoverSide}
      align={popoverAlign}
      sideOffset={sideOffset}
      alignOffset={popoverAlignOffset}
      triggerHidden={triggerHidden}
      triggerClassName={triggerClassName}
      optionsContainerClassName={optionsContainerClassName}
      anchorToTriggerEnd={anchorToTriggerEnd}
      avoidCollisions={avoidCollisions}
      onValueChange={(val) => {
        setOutLang?.(val);
      }}
    />
  );
};
